import api from './client';

export type ManualTtnCargoRow = {
  name?: string;
  boxType?: string;
  quantity: number;
  weight?: number | string;
  volume?: number | string;
  length?: number | string;
  width?: number | string;
  height?: number | string;
};

export type NamedRef = { id: string; fullname: string } | null;

export type PortalOrder = {
  id: string;
  invoice: string;
  orderType: 'regional' | 'city';
  status: number;
  waybillNumber: string | null;
  awaitingTtn: boolean;
  deliveryMode: string | null;
  notes: string | null;
  orderDatetime: string | null;
  plannedPickupDate?: string | null;
  seatsCount: number;
  cargoBarcodes?: string[];
  totalWeight?: number | null;
  totalVolume?: number | null;
  sBranch: NamedRef;
  rBranch: NamedRef;
  cityFrom: NamedRef;
  cityTo: NamedRef;
  createdByName?: string | null;
  progressLabel?: string | null;
  trackPath?: string | null;
  /** Branch user: false when their branch is only the receiver. */
  canAttachTtn?: boolean;
};

export type PortalBranchRef = {
  id: string;
  fullname: string;
  cityId?: string | null;
  city?: { id: string; fullname: string } | null;
};

export type PortalMe = {
  userId: string;
  fullname: string;
  username: string;
  clientId: string | null;
  client: { id: string; name: string; fullname?: string | null; bin?: string | null } | null;
  branchId: string | null;
  branch: PortalBranchRef | null;
  canManageAccounts: boolean;
};

export type BranchAccount = {
  id: string;
  username: string;
  fullname: string;
  isActive: number;
  createdAt: string;
  portalBranch: { id: string; fullname: string } | null;
};

export type CreateBranchAccountPayload = {
  username: string;
  password: string;
  fullname: string;
  branchId: string;
};

export type CreatePreorderPayload = {
  orderType: 'regional' | 'city';
  sBranchId: string;
  rBranchId: string;
  cityFromId?: string;
  cityToId?: string;
  senderId?: string;
  deliveryMode?: 'MAIN' | 'TRANSFER' | 'RETURN' | 'CROSS_DOCK';
  notes?: string;
  seatsCount?: number;
  volume?: number;
  plannedPickupDate?: string;
  cargoRows?: ManualTtnCargoRow[];
  cargoBarcodes?: string[];
  confirm: boolean;
};

export type PortalBoxType = {
  id: string;
  boxType: string;
  length: number | null;
  width: number | null;
  height: number | null;
  weight: number | string | null;
};

export type PortalImportResult = {
  createdCount: number;
  created: Array<{ id: string; invoice: string }>;
  skipped: Array<{ row: number; message: string }>;
  errors: Array<{ row: number; message: string }>;
};

export type AttachTtnPayload = {
  waybillNumber: string;
  waybillDate?: string;
  seatsCount: number;
  cargoRows: ManualTtnCargoRow[];
  notes?: string;
  confirm: boolean;
};

const PORTAL_STATUS_LABEL: Record<number, string> = {
  0: 'Предзаказ',
  1: 'Забор с нашего склада',
  2: 'Принято на складе',
  3: 'В пути',
  4: 'Принято на транзитном складе',
  5: 'Отправлено с транзитного склада',
  6: 'Доставлено',
  7: 'Отменено',
  8: 'Забор с СВХ',
  9: 'Забор со склада клиента',
  10: 'В пути на склад РП',
  11: 'На складе РП',
  12: 'Перегрузка',
  13: 'В пути к получателю',
  14: 'В пути на хаб',
  15: 'На складе хаба',
};

export function portalStatusLabel(o: Pick<PortalOrder, 'status' | 'awaitingTtn' | 'progressLabel' | 'waybillNumber'>): string {
  if (o.progressLabel) return o.progressLabel;
  if (o.awaitingTtn || (o.status === 0 && !String(o.waybillNumber || '').trim())) return 'Ждёт ТТН';
  return PORTAL_STATUS_LABEL[o.status] ?? 'Предзаказ';
}

export function portalModeLabel(mode?: string | null): string {
  if (mode === 'TRANSFER') return 'Между филиалами';
  if (mode === 'RETURN') return 'Возврат стока';
  if (mode === 'CROSS_DOCK') return 'Перегруз';
  if (mode === 'MAIN') return 'Основная перевозка';
  return mode || '—';
}

export type PortalHistoryItem = {
  id: string;
  at: string;
  actor: string | null;
  label: string;
};

export const clientPortalApi = {
  me: async () => (await api.get<PortalMe>('/client-portal/me')).data,

  listAccounts: async () =>
    (await api.get<BranchAccount[]>('/client-portal/accounts')).data,

  createAccount: async (body: CreateBranchAccountPayload) =>
    (await api.post<Pick<BranchAccount, 'id' | 'username' | 'fullname' | 'portalBranch'>>(
      '/client-portal/accounts',
      body,
    )).data,

  cities: async () =>
    (await api.get<Array<{ id: string; fullname: string }>>('/client-portal/cities')).data,

  branches: async (clientId: string, cityId?: string) =>
    (await api.get<Array<{
      id: string;
      fullname: string;
      adress?: string | null;
      cityId?: string | null;
      city?: { id: string; fullname: string } | null;
    }>>('/client-portal/branches', { params: { clientId, cityId } })).data,

  boxTypes: async () =>
    (await api.get<PortalBoxType[]>('/client-portal/box-types')).data,

  listOrders: async (awaitingTtn?: boolean) =>
    (await api.get<PortalOrder[]>('/client-portal/orders', {
      params: awaitingTtn === undefined ? {} : { awaitingTtn: awaitingTtn ? '1' : '0' },
    })).data,

  exportOrdersWithTtn: async () =>
    (await api.get<Blob>('/client-portal/orders/export', { responseType: 'blob' })).data,

  getOrder: async (id: string) =>
    (await api.get<PortalOrder>(`/client-portal/orders/${id}`)).data,

  history: async (id: string) =>
    (await api.get<PortalHistoryItem[]>(`/client-portal/orders/${id}/history`)).data,

  createPreorder: async (body: CreatePreorderPayload) =>
    (await api.post<PortalOrder>('/client-portal/preorders', body)).data,

  updatePreorder: async (id: string, body: { cargoBarcodes: string[]; seatsCount?: number }) =>
    (await api.patch<PortalOrder>(`/client-portal/orders/${id}`, body)).data,

  downloadImportTemplate: async () =>
    (await api.get<Blob>('/client-portal/preorders/import-template', { responseType: 'blob' })).data,

  importPreorders: async (file: File) => {
    const body = new FormData();
    body.append('file', file);
    return (await api.post<PortalImportResult>('/client-portal/preorders/import', body)).data;
  },

  attachTtn: async (id: string, body: AttachTtnPayload) =>
    (await api.post<PortalOrder>(`/client-portal/orders/${id}/ttn`, body)).data,

  cancelPreorder: async (id: string) =>
    (await api.post<{ ok: boolean; id: string }>(`/client-portal/orders/${id}/cancel`)).data,
};
